import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronRight, Settings } from "lucide-react";
import ExcelGrid from "components/ExcelGrid";
import Page from "components/Page";
import SectionCard from "components/SectionCard";
import TopTabMenu from "components/TopTabMenu";
import { DropdownSelect } from "components/InputStyled";
import { useDialog } from "context/DialogContext";
import { useProfile } from "context/ProfileContext";
import useUserPreferences from "hooks/useUserPreferences";
import { buildIssueCouponScheduleFromDeal } from "pages/DebtStudio/lib/buildPortfolioDebtService";
import { fromCents, toCents } from "./lib/allocation";
import {
  buildAllocationPivot,
  buildOutstandingBalanceRecords,
  buildPortfolioAllocationDebtServiceRecords,
  expandReportingDimensions,
  flattenAllocationHierarchy,
  getAllocationCopyRows,
  ISSUE_DIMENSION,
  PAYMENT_DATE_DIMENSION,
  PROJECT_DIMENSION,
} from "./lib/reporting";
import AllocationReportSettingsModal from "./components/AllocationReportSettingsModal";
import AllocationOverviewChart from "./components/AllocationOverviewChart";
import exportCapitalFlowOverviewPdf from "./reports/exportCapitalFlowOverviewPdf";
import {
  fetchClassifications,
  fetchDebtAllocationPortfolio,
  fetchDebtStudioIssues,
  fetchProjects,
} from "./services/capitalFlowService";

const currency = (value) => Number(value || 0).toLocaleString(undefined, {
  style: "currency", currency: "USD", minimumFractionDigits: 2,
});

const moneyColumn = (key, label) => ({
  key, label, editable: false, numeric: true, width: "w-40",
  className: "text-right numeric", headerClassName: "text-right", format: currency,
});
const REPORTING_PRESET_CATEGORY = "capital_flow.reporting_preset";
const OVERVIEW_PRESET_CATEGORY = "capital_flow.overview_preset";
const ALLOCATION_DETAILS_ENABLED = false;
const presetKeyPrefix = (orgId) => `overview:${orgId}:`;
const overviewPresetKeyPrefix = (orgId) => `chart:${orgId}:`;
const todayIso = () => {
  const date = new Date();
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
};

export default function CapitalFlowOverview() {
  const { selectedOrg, profile } = useProfile();
  const { alert, confirm, prompt } = useDialog();
  const { preferences, loading: presetsLoading, setPreference, removePreference } = useUserPreferences(profile?.id);
  const [issues, setIssues] = useState([]);
  const [projects, setProjects] = useState([]);
  const [classifications, setClassifications] = useState([]);
  const [workspace, setWorkspace] = useState({ components: [], assignments: [], amounts: [] });
  const [reportConfig, setReportConfig] = useState({
    rowDimensions: [PROJECT_DIMENSION], columnDimension: "",
    measures: ["allocatedPrincipal", "allocatedInterest", "allocatedDebtService", "outstandingBalance"],
    startDate: "", endDate: "", asOfDate: todayIso(),
  });
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [activeView, setActiveView] = useState("overview");
  const [overviewContext, setOverviewContext] = useState(null);
  const [expandedKeys, setExpandedKeys] = useState(new Set());
  const [selectedPresetKey, setSelectedPresetKey] = useState("");
  const [selectedOverviewPresetKey, setSelectedOverviewPresetKey] = useState("");
  const [overviewPresetRequest, setOverviewPresetRequest] = useState(null);
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);

  const reportClassifications = useMemo(() => classifications.filter((classification) =>
    classification.scopes.some((scope) => scope.object_type === "debt_allocation_component")
  ), [classifications]);
  const dimensionOptions = useMemo(() => [
    ...reportClassifications.map((classification) => ({ value: classification.id, label: classification.name })),
    { value: ISSUE_DIMENSION, label: "Debt Issue" },
    { value: PROJECT_DIMENSION, label: "Project" },
    { value: PAYMENT_DATE_DIMENSION, label: "Payment Date" },
  ], [reportClassifications]);
  const reportingPresets = useMemo(() => {
    const prefix = presetKeyPrefix(selectedOrg?.id || "");
    return preferences
      .filter((preference) =>
        preference.preference_category === REPORTING_PRESET_CATEGORY &&
        preference.preference_key.startsWith(prefix)
      )
      .map((preference) => ({
        key: preference.preference_key,
        ...preference.preference_value,
      }))
      .filter((preset) => preset.name)
      .sort((left, right) => left.name.localeCompare(right.name));
  }, [preferences, selectedOrg?.id]);
  const overviewPresets = useMemo(() => {
    const prefix = overviewPresetKeyPrefix(selectedOrg?.id || "");
    return preferences
      .filter((preference) => preference.preference_category === OVERVIEW_PRESET_CATEGORY && preference.preference_key.startsWith(prefix))
      .map((preference) => ({ key: preference.preference_key, ...preference.preference_value }))
      .filter((preset) => preset.name)
      .sort((left, right) => left.name.localeCompare(right.name));
  }, [preferences, selectedOrg?.id]);
  useEffect(() => { setSelectedPresetKey(""); }, [selectedOrg?.id]);
  useEffect(() => { setSelectedOverviewPresetKey(""); setOverviewPresetRequest(null); }, [selectedOrg?.id]);
  useEffect(() => { setOverviewContext(null); }, [selectedOrg?.id]);

  const loadOrganization = useCallback(async () => {
    if (!selectedOrg?.id) return;
    try {
      const [nextIssues, nextProjects, nextClassifications, nextWorkspace] = await Promise.all([
        fetchDebtStudioIssues(selectedOrg.id), fetchProjects(selectedOrg.id), fetchClassifications(selectedOrg.id), fetchDebtAllocationPortfolio(selectedOrg.id),
      ]);
      setIssues(nextIssues); setProjects(nextProjects); setClassifications(nextClassifications);
      setWorkspace(nextWorkspace);
    } catch (error) {
      await alert(error.message, { title: "Unable to Load CapitalFlow Reporting" });
    }
  }, [alert, selectedOrg?.id]);
  useEffect(() => { loadOrganization(); }, [loadOrganization]);

  const hasDateRange = reportConfig.rowDimensions.includes(PAYMENT_DATE_DIMENSION) || reportConfig.columnDimension === PAYMENT_DATE_DIMENSION;
  const activeStartDate = hasDateRange ? reportConfig.startDate : "";
  const activeEndDate = hasDateRange ? reportConfig.endDate : "";
  const applicableIssueIds = useMemo(() => new Set(workspace.components.map((item) => item.ds_deal_id)), [workspace.components]);
  const applicableIssues = useMemo(() => issues.filter((item) => applicableIssueIds.has(item.id)), [applicableIssueIds, issues]);
  const overviewDebtServiceRecords = useMemo(() => buildPortfolioAllocationDebtServiceRecords({ issues: applicableIssues, workspace }), [applicableIssues, workspace]);
  const actualTotals = useMemo(() => applicableIssues.flatMap(buildIssueCouponScheduleFromDeal).filter((row) => {
    const date = String(row.date || "").slice(0, 10);
    return (!activeStartDate || date >= activeStartDate) && (!activeEndDate || date <= activeEndDate);
  }).reduce((total, row) => ({ principal: total.principal + Number(row.principal || 0), interest: total.interest + Number(row.interest || 0) }), { principal: 0, interest: 0 }), [activeEndDate, activeStartDate, applicableIssues]);
  const debtServiceRecords = useMemo(() => buildPortfolioAllocationDebtServiceRecords({ issues: applicableIssues, workspace, startDate: activeStartDate, endDate: activeEndDate }), [activeEndDate, activeStartDate, applicableIssues, workspace]);
  const displayedPaymentDates = useMemo(() => hasDateRange
    ? [...new Set(debtServiceRecords.map((record) => record.paymentDate))]
    : [], [debtServiceRecords, hasDateRange]);
  const outstandingBalanceRecords = useMemo(() => reportConfig.measures.includes("outstandingBalance")
    ? buildOutstandingBalanceRecords({
      issues: applicableIssues,
      workspace,
      asOfDate: reportConfig.asOfDate || todayIso(),
      asOfDates: hasDateRange ? displayedPaymentDates : [],
    })
    : [], [applicableIssues, displayedPaymentDates, hasDateRange, reportConfig.asOfDate, reportConfig.measures, workspace]);
  const records = useMemo(() => [...debtServiceRecords, ...outstandingBalanceRecords], [debtServiceRecords, outstandingBalanceRecords]);
  const allocatedTotals = useMemo(() => debtServiceRecords.reduce((total, row) => ({ principalCents: total.principalCents + row.allocatedCents, interestCents: total.interestCents + row.interestCents }), { principalCents: 0, interestCents: 0 }), [debtServiceRecords]);
  const actualDebtServiceCents = toCents(actualTotals.principal + actualTotals.interest);
  const allocatedDebtServiceCents = allocatedTotals.principalCents + allocatedTotals.interestCents;
  const handleOverviewContextChange = useCallback((nextContext) => setOverviewContext(nextContext), []);
  const overviewKpis = useMemo(() => {
    const context = overviewContext || { measure: "allocatedDebtService", view: "category", startDate: todayIso(), endDate: "", asOfDate: todayIso(), records: overviewDebtServiceRecords.filter((record) => record.paymentDate >= todayIso()) };
    const contextRecords = context.records || [];
    const projectCount = new Set(contextRecords.map((record) => record.projectId).filter(Boolean)).size;
    const issueIds = new Set(contextRecords.map((record) => record.issueId).filter(Boolean));
    const contextIssues = applicableIssues.filter((issue) => issueIds.has(issue.id));
    if (context.measure === "outstandingBalance") {
      const latestRecordDate = contextRecords.reduce((latest, record) => String(record.balanceAsOfDate || "") > latest ? String(record.balanceAsOfDate) : latest, "");
      const balanceDate = context.view === "time" ? latestRecordDate : context.asOfDate;
      const totalOutstandingCents = contextIssues.flatMap(buildIssueCouponScheduleFromDeal)
        .filter((row) => String(row.date || "").slice(0, 10) > balanceDate)
        .reduce((sum, row) => sum + toCents(row.principal), 0);
      const allocatedOutstandingCents = contextRecords.reduce((sum, record) => String(record.balanceAsOfDate || "") === balanceDate ? sum + Number(record.outstandingBalanceCents || 0) : sum, 0);
      return { projectCount, issueCount: issueIds.size, totalLabel: "Total Outstanding Principal", allocatedLabel: "Allocated Outstanding Principal", varianceLabel: "Unallocated Outstanding Principal", totalCents: totalOutstandingCents, allocatedCents: allocatedOutstandingCents, varianceCents: totalOutstandingCents - allocatedOutstandingCents };
    }
    const totalDebtServiceCents = contextIssues.flatMap(buildIssueCouponScheduleFromDeal)
      .filter((row) => { const date = String(row.date || "").slice(0, 10); return (!context.startDate || date >= context.startDate) && (!context.endDate || date <= context.endDate); })
      .reduce((sum, row) => sum + toCents(Number(row.principal || 0) + Number(row.interest || 0)), 0);
    const allocatedCents = contextRecords.reduce((sum, record) => sum + Number(record.allocatedCents || 0) + Number(record.interestCents || 0), 0);
    return { projectCount, issueCount: issueIds.size, totalLabel: "Total Debt Service", allocatedLabel: "Allocated Debt Service", varianceLabel: "Under/(Over) Allocated", totalCents: totalDebtServiceCents, allocatedCents, varianceCents: totalDebtServiceCents - allocatedCents };
  }, [applicableIssues, overviewContext, overviewDebtServiceRecords]);
  const displayedKpis = useMemo(() => !ALLOCATION_DETAILS_ENABLED || activeView === "overview" ? overviewKpis : { projectCount: projects.length, issueCount: applicableIssues.length, totalLabel: "Total Debt Service", allocatedLabel: "Allocated Debt Service", varianceLabel: "Under/(Over) Allocated", totalCents: actualDebtServiceCents, allocatedCents: allocatedDebtServiceCents, varianceCents: actualDebtServiceCents - allocatedDebtServiceCents }, [activeView, actualDebtServiceCents, allocatedDebtServiceCents, applicableIssues.length, overviewKpis, projects.length]);
  const handleOverviewPdfExport = useCallback(async () => {
    if (!selectedOrg?.id || !overviewContext) return;
    try {
      setIsPdfGenerating(true);
      await exportCapitalFlowOverviewPdf({
        orgId: selectedOrg.id,
        orgName: selectedOrg.name || "",
        context: overviewContext,
        kpis: [
          { label: "Projects", value: displayedKpis.projectCount },
          { label: "Applicable Debt Issues", value: displayedKpis.issueCount },
          { label: displayedKpis.allocatedLabel, value: currency(fromCents(displayedKpis.allocatedCents)) },
        ],
      });
    } catch (error) {
      await alert(error?.message || "Unable to export the CapitalFlow Overview PDF.", { title: "PDF export failed" });
    } finally {
      setIsPdfGenerating(false);
    }
  }, [alert, displayedKpis, overviewContext, selectedOrg]);
  const pivot = useMemo(() => buildAllocationPivot({
    records, rowDimensions: reportConfig.rowDimensions, columnDimension: reportConfig.columnDimension,
    projects, classifications: reportClassifications,
  }), [projects, records, reportClassifications, reportConfig.columnDimension, reportConfig.rowDimensions]);
  const hierarchy = pivot.hierarchy;
  const allExpandableKeys = useMemo(() => {
    const keys = [];
    const visit = (nodes) => (nodes || []).forEach((node) => {
      if (node.is_expandable) keys.push(node.key);
      visit(node.children);
    });
    visit(hierarchy);
    return keys;
  }, [hierarchy]);
  useEffect(() => { setExpandedKeys(new Set(allExpandableKeys)); }, [allExpandableKeys]);
  const visibleHierarchyRows = useMemo(() => flattenAllocationHierarchy(hierarchy, expandedKeys), [expandedKeys, hierarchy]);
  const toggleExpanded = useCallback((key) => setExpandedKeys((current) => {
    const next = new Set(current);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  }), []);

  const measureLabels = useMemo(() => ({ allocatedPrincipal: "Allocated Principal", allocatedInterest: "Allocated Interest", allocatedDebtService: "Allocated Debt Service", outstandingBalance: "Outstanding Balance" }), []);
  const columnGroups = useMemo(() => reportConfig.columnDimension
    ? [...pivot.columnGroups, { key: "__total", label: "Total", total: true }]
    : [{ key: "__total", label: "", total: true }], [pivot.columnGroups, reportConfig.columnDimension]);
  const groupingColumn = useMemo(() => ({
    key: "grouping", label: "Grouping / Subtotal", editable: false, width: "w-96",
    render: (row) => <div className="flex items-center gap-2" style={{ paddingLeft: `${4 + Number(row.depth || 0) * 18}px` }}>{row.showExpander && row.is_expandable ? <button type="button" className="inline-flex h-5 w-5 items-center justify-center rounded hover:bg-muted/30" onClick={(event) => { event.stopPropagation(); toggleExpanded(row.key); }} title={expandedKeys.has(row.key) ? "Collapse" : "Expand"}>{expandedKeys.has(row.key) ? <ChevronDown size={16} className="text-muted" /> : <ChevronRight size={16} className="text-muted" />}</button> : <span className="inline-block w-5" />}<span className="truncate">{row.displayLabel ?? row.label}</span></div>,
    copyValue: (row) => row.displayLabel ?? row.label,
  }), [expandedKeys, toggleExpanded]);
  const groupedColumns = useMemo(() => {
    return [groupingColumn, ...columnGroups.flatMap((group) => reportConfig.measures
      .filter((measure) => !(
        group.total &&
        reportConfig.columnDimension === PAYMENT_DATE_DIMENSION &&
        measure === "outstandingBalance"
      ))
      .map((measure) => ({
        ...moneyColumn(`${group.key}:${measure}`, measureLabels[measure]),
        headerGroup: reportConfig.columnDimension ? group.label : "",
        headerGroupClassName: "whitespace-normal break-words leading-tight",
      })))];
  }, [columnGroups, groupingColumn, measureLabels, reportConfig.columnDimension, reportConfig.measures]);
  const groupedRows = useMemo(() => {
    return visibleHierarchyRows.map((row) => {
      const values = {};
      columnGroups.forEach((group) => reportConfig.measures.forEach((measure) => { values[`${group.key}:${measure}`] = group.total ? row[measure] : row.cells?.[group.key]?.[measure] || 0; }));
      return { ...row, ...values, showExpander: true };
    });
  }, [columnGroups, reportConfig.measures, visibleHierarchyRows]);
  const copyRowClassifications = useMemo(
    () => expandReportingDimensions(reportConfig.rowDimensions, reportClassifications),
    [reportClassifications, reportConfig.rowDimensions]
  );
  const copyTableColumns = useMemo(() => [
    ...copyRowClassifications.map((descriptor, index) => ({
      key: `group_${index}`,
      label: descriptor.classificationLevel != null
        ? descriptor.label.replace(" Level ", " - Level ")
        : dimensionOptions.find((option) => option.value === descriptor.sourceDimension)?.label || descriptor.label,
    })),
    ...groupedColumns.slice(1),
  ], [copyRowClassifications, dimensionOptions, groupedColumns]);
  const copyTableRows = useMemo(() => getAllocationCopyRows(pivot.hierarchy).map((row) => {
    const values = {};
    columnGroups.forEach((group) => reportConfig.measures.forEach((measure) => {
      values[`${group.key}:${measure}`] = group.total ? row[measure] : row.cells?.[group.key]?.[measure] || 0;
    }));
    return { ...row, ...values };
  }), [columnGroups, pivot.hierarchy, reportConfig.measures]);
  const copyTableHeading = `CapitalFlow Allocation Reporting${activeStartDate || activeEndDate ? ` — Date Range ${activeStartDate || "Beginning"} through ${activeEndDate || "End"}` : " — All Dates"}`;
  const isAllExpanded = allExpandableKeys.length > 0 && allExpandableKeys.every((key) => expandedKeys.has(key));
  const isAllCollapsed = expandedKeys.size === 0;

  const getPresetConfig = (key) => {
    const preset = reportingPresets.find((item) => item.key === key);
    if (!preset) return null;
    const validDimensions = new Set(dimensionOptions.map((option) => option.value));
    const rowDimensions = (preset.rowDimensions || preset.dimensions || []).filter((dimension) => validDimensions.has(dimension));
    const columnDimension = validDimensions.has(preset.columnDimension) && !rowDimensions.includes(preset.columnDimension) ? preset.columnDimension : "";
    return {
      rowDimensions, columnDimension,
      measures: (preset.measures || ["allocatedPrincipal", "allocatedInterest", "allocatedDebtService", "outstandingBalance"]).filter((measure) => ["allocatedPrincipal", "allocatedInterest", "allocatedDebtService", "outstandingBalance"].includes(measure)),
      startDate: preset.startDate || "", endDate: preset.endDate || "", asOfDate: preset.asOfDate || todayIso(),
    };
  };

  const applyPreset = (key) => {
    setSelectedPresetKey(key);
    if (!key) return;
    const presetConfig = getPresetConfig(key);
    if (presetConfig) setReportConfig(presetConfig);
  };

  const savePreset = async (configToSave = reportConfig, presetKey = "") => {
    const presetToUpdate = reportingPresets.find((preset) => preset.key === presetKey) || null;
    const name = (await prompt("Save the current grouping parameters and order as a personal reporting preset.", {
      title: presetToUpdate ? "Update Reporting Preset" : "Save Reporting Preset",
      label: "Preset name",
      defaultValue: presetToUpdate?.name || "",
      okLabel: "Save",
    }))?.trim();
    if (!name) return null;
    const matchingPreset = reportingPresets.find((preset) =>
      preset.name.toLocaleLowerCase() === name.toLocaleLowerCase()
    );
    const key = presetKey || matchingPreset?.key ||
      `${presetKeyPrefix(selectedOrg.id)}${globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`}`;
    try {
      await setPreference(REPORTING_PRESET_CATEGORY, key, {
        version: 2,
        name,
        ...configToSave,
        startDate: configToSave.rowDimensions.includes(PAYMENT_DATE_DIMENSION) || configToSave.columnDimension === PAYMENT_DATE_DIMENSION ? configToSave.startDate : "",
        endDate: configToSave.rowDimensions.includes(PAYMENT_DATE_DIMENSION) || configToSave.columnDimension === PAYMENT_DATE_DIMENSION ? configToSave.endDate : "",
        asOfDate: configToSave.asOfDate || todayIso(),
      });
      return key;
    } catch (error) {
      await alert(error.message, { title: "Unable to Save Reporting Preset" });
      return null;
    }
  };

  const deletePreset = async (presetKey) => {
    const presetToDelete = reportingPresets.find((preset) => preset.key === presetKey);
    if (!presetToDelete) return false;
    const approved = await confirm(`Delete the reporting preset “${presetToDelete.name}”?`, {
      title: "Delete Reporting Preset",
      okLabel: "Delete",
    });
    if (!approved) return false;
    try {
      await removePreference(REPORTING_PRESET_CATEGORY, presetToDelete.key);
      if (selectedPresetKey === presetToDelete.key) setSelectedPresetKey("");
      return true;
    } catch (error) {
      await alert(error.message, { title: "Unable to Delete Reporting Preset" });
      return false;
    }
  };

  const selectOverviewPreset = (key) => {
    setSelectedOverviewPresetKey(key);
    const preset = overviewPresets.find((item) => item.key === key);
    if (preset) setOverviewPresetRequest({ key, config: preset, requestId: Date.now() });
  };

  const saveOverviewPreset = async (config, suggestedName = "") => {
    const name = (await prompt("Save the current Allocation Overview configuration as a personal preset.", {
      title: "Save New Overview Preset",
      label: "Preset name",
      defaultValue: suggestedName,
      okLabel: "Save",
    }))?.trim();
    if (!name) return null;
    const matchingPreset = overviewPresets.find((preset) => preset.name.toLocaleLowerCase() === name.toLocaleLowerCase());
    if (matchingPreset) {
      await alert(`A preset named “${matchingPreset.name}” already exists. Choose a different name.`, { title: "Preset Name Already Used" });
      return null;
    }
    const key = `${overviewPresetKeyPrefix(selectedOrg.id)}${globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`}`;
    try {
      await setPreference(OVERVIEW_PRESET_CATEGORY, key, { ...config, name });
      setSelectedOverviewPresetKey(key);
      return key;
    } catch (error) {
      await alert(error.message, { title: "Unable to Save Overview Preset" });
      return null;
    }
  };

  const updateOverviewPreset = async (presetKey, config) => {
    const preset = overviewPresets.find((item) => item.key === presetKey);
    if (!preset) return false;
    try {
      await setPreference(OVERVIEW_PRESET_CATEGORY, preset.key, { ...config, name: preset.name });
      return true;
    } catch (error) {
      await alert(error.message, { title: "Unable to Update Overview Preset" });
      return false;
    }
  };

  const renameOverviewPreset = async (presetKey) => {
    const preset = overviewPresets.find((item) => item.key === presetKey);
    if (!preset) return false;
    const name = (await prompt("Rename this personal Allocation Overview preset.", {
      title: "Rename Overview Preset",
      label: "Preset name",
      defaultValue: preset.name,
      okLabel: "Rename",
    }))?.trim();
    if (!name || name === preset.name) return false;
    const duplicate = overviewPresets.find((item) => item.key !== preset.key && item.name.toLocaleLowerCase() === name.toLocaleLowerCase());
    if (duplicate) {
      await alert(`A preset named “${duplicate.name}” already exists. Choose a different name.`, { title: "Preset Name Already Used" });
      return false;
    }
    try {
      const presetValue = { ...preset };
      delete presetValue.key;
      await setPreference(OVERVIEW_PRESET_CATEGORY, preset.key, { ...presetValue, name });
      return true;
    } catch (error) {
      await alert(error.message, { title: "Unable to Rename Overview Preset" });
      return false;
    }
  };

  const deleteOverviewPreset = async (presetKey) => {
    const preset = overviewPresets.find((item) => item.key === presetKey);
    if (!preset) return false;
    if (!await confirm(`Delete the Overview preset “${preset.name}”?`, { title: "Delete Overview Preset", okLabel: "Delete" })) return false;
    try {
      await removePreference(OVERVIEW_PRESET_CATEGORY, preset.key);
      if (selectedOverviewPresetKey === preset.key) setSelectedOverviewPresetKey("");
      return true;
    } catch (error) {
      await alert(error.message, { title: "Unable to Delete Overview Preset" });
      return false;
    }
  };

  return (
    <Page title="CapitalFlow - Overview" subtitle="Reconcile debt principal and analyze allocations by any combination of project classifications." showOrganizationSelector>
      {selectedOrg ? <>
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          <SectionCard title="Projects" compact><div className="text-lg font-semibold tabular-nums text-app">{displayedKpis.projectCount}</div></SectionCard>
          <SectionCard title="Applicable Debt Issues" compact><div className="text-lg font-semibold tabular-nums text-app">{displayedKpis.issueCount}</div></SectionCard>
          <SectionCard title={displayedKpis.allocatedLabel} compact><div className="text-lg font-semibold tabular-nums text-app">{currency(fromCents(displayedKpis.allocatedCents))}</div></SectionCard>
        </div>

        <div>
          {ALLOCATION_DETAILS_ENABLED ? <TopTabMenu tabs={[{ key: "overview", label: "Overview" }, { key: "details", label: "Details" }]} activeTab={activeView} onChange={setActiveView} ariaLabel="CapitalFlow reporting views" actions={activeView === "details" ? <button type="button" className="btn btn-ghost inline-flex items-center gap-2" onClick={() => setSettingsOpen(true)} title="Report settings" aria-label="Report settings"><Settings size={20} /><span className="hidden sm:inline">Report settings</span></button> : null} /> : null}
          <div {...(ALLOCATION_DETAILS_ENABLED ? { role: "tabpanel", id: `tabpanel-${activeView}`, "aria-labelledby": `tab-${activeView}` } : {})}>
            {!ALLOCATION_DETAILS_ENABLED || activeView === "overview" ? (
              <SectionCard title="Allocation Overview" subtitle="Configure an interactive view of the complete allocation data set" headerAction={<button type="button" className="btn btn-primary shrink-0 text-sm" onClick={handleOverviewPdfExport} disabled={!overviewContext || isPdfGenerating} title="Export the configured CapitalFlow Overview report to PDF">{isPdfGenerating ? "Generating PDF..." : "Export PDF"}</button>}>
                <AllocationOverviewChart flowRecords={overviewDebtServiceRecords} dimensionOptions={dimensionOptions} projects={projects} classifications={reportClassifications} issues={applicableIssues} workspace={workspace} onContextChange={handleOverviewContextChange} reportingPresets={overviewPresets} presetsLoading={presetsLoading} selectedPresetKey={selectedOverviewPresetKey} presetRequest={overviewPresetRequest} onSelectPreset={selectOverviewPreset} onSavePreset={saveOverviewPreset} onUpdatePreset={updateOverviewPreset} onRenamePreset={renameOverviewPreset} onDeletePreset={deleteOverviewPreset} />
              </SectionCard>
            ) : (
              <SectionCard title="Allocation Details" subtitle="Allocation metrics by the configured row and column classifications">
                <div className="mb-4 max-w-sm"><DropdownSelect label="Reporting preset" value={selectedPresetKey} onChange={applyPreset} options={reportingPresets.map((preset) => ({ value: preset.key, label: preset.name }))} placeholder={presetsLoading ? "Loading presets…" : "Select a saved preset"} disabled={presetsLoading} /></div>
                <ExcelGrid columns={groupedColumns} rows={groupedRows} copyTableColumns={copyTableColumns} copyTableRows={copyTableRows} copyTableHeading={copyTableHeading} copyTableAsHtml toolbarActions={<><button type="button" className="linkCls text-xs disabled:cursor-default disabled:opacity-50 disabled:no-underline" disabled={isAllExpanded} onClick={() => setExpandedKeys(new Set(allExpandableKeys))}>Expand all</button><button type="button" className="linkCls text-xs disabled:cursor-default disabled:opacity-50 disabled:no-underline" disabled={isAllCollapsed} onClick={() => setExpandedKeys(new Set())}>Collapse all</button></>} rowClassName={(row) => row.isSubtotal ? "font-semibold bg-muted/20" : ""} viewportConstrained />
              </SectionCard>
            )}
          </div>
        </div>
        {settingsOpen ? <AllocationReportSettingsModal config={reportConfig} dimensionOptions={dimensionOptions} reportingPresets={reportingPresets} selectedPresetKey={selectedPresetKey} presetsLoading={presetsLoading} onLoadPreset={getPresetConfig} onSavePreset={savePreset} onDeletePreset={deletePreset} onApply={(next, presetKey) => { setReportConfig(next); setSelectedPresetKey(presetKey); setSettingsOpen(false); }} onClose={() => setSettingsOpen(false)} /> : null}
      </> : null}
    </Page>
  );
}
